import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Globe, MessageCircle, MessageSquare, Heart, Share2, Flag, MapPin, Sparkles, Star, Pencil, Trash2 } from "lucide-react";
import api, { apiError, fileUrl, PLACEHOLDER_IMG } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReportDialog, ShareDialog, CheckoutDialog } from "@/components/dialogs";
import { VerifBadge, Rating, PageLoading, EmptyState, DemoTag, BlockedScreen } from "@/components/common";

export default function BusinessDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const gate = useAuthGate();
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState(false);
  const [report, setReport] = useState(false);
  const [share, setShare] = useState(false);
  const [feature, setFeature] = useState(false);
  const [product, setProduct] = useState(null);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editRating, setEditRating] = useState(5);

  const load = () => api.get(`/businesses/${id}`).then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setData("blocked"); });
  useEffect(() => { load(); api.get("/payments/products").then(({ data }) => setProduct(data.items.find((p) => p.lookup_key === "featured_business"))); }, [id]);

  if (data === "blocked") return <BlockedScreen onBack={() => nav(-1)} />;
  if (!data) return <PageLoading />;
  const b = data.business;
  const isOwner = user && b.owner_id === user.id;
  const featured = b.featured_until && b.featured_until > new Date().toISOString();
  const myReview = data.my_review || (user ? data.reviews.find((r) => r.reviewer_id === user.id) : null);
  const reviews = myReview && !data.reviews.some((r) => r.id === myReview.id) ? [myReview, ...data.reviews] : data.reviews;

  const track = (action, url) => { api.post(`/businesses/${id}/track`, { action }); if (url) window.open(url, "_blank"); };
  const save = async () => {
    if (!gate.require("Create a free account to save businesses and connect with the community.")) return;
    try { if (saved) { await api.delete("/favourites", { params: { item_type: "business", item_id: id } }); setSaved(false); } else { await api.post("/favourites", { item_type: "business", item_id: id }); setSaved(true); toast.success("Saved"); } } catch (e) { toast.error(apiError(e)); }
  };
  const message = async () => {
    if (!gate.require("Create a free account to message businesses on Mtaa.")) return;
    if (isOwner) return toast.info("This is your business.");
    try { const { data } = await api.post("/conversations", { recipient_id: b.owner_id, context_type: "business", context_id: id, context_label: b.name, message: `Hi, I'm interested in ${b.name}.` }); nav(`/messages/${data.conversation_id}`); } catch (e) { toast.error(apiError(e)); }
  };
  const submitReview = async () => {
    try { await api.post("/reviews", { target_type: "business", target_id: id, rating: reviewRating, text: reviewText }); toast.success("Review posted"); setReviewText(""); setReviewRating(5); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const startEdit = (r) => { setEditRating(r.rating); setEditText(r.text || ""); setEditing(true); };
  const saveEdit = async (rid) => {
    try { await api.put(`/reviews/${rid}`, { rating: editRating, text: editText }); toast.success("Review updated"); setEditing(false); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const deleteReview = async (rid) => {
    if (!window.confirm("Delete your review? This cannot be undone.")) return;
    try { await api.delete(`/reviews/${rid}`); toast.success("Review deleted"); setEditing(false); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="overflow-hidden rounded-3xl border border-border bg-card">
        <div className="relative h-48 bg-muted">
          <img src={fileUrl(b.cover_url) || PLACEHOLDER_IMG} alt={b.name} className="h-full w-full object-cover" />
          {featured && <span className="absolute left-3 top-3 rounded-md bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">FEATURED</span>}
          {b.is_demo && <span className="absolute right-3 top-3"><DemoTag /></span>}
        </div>
        <div className="space-y-3 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">{b.name}</h1>
              <p className="text-sm font-medium text-primary">{b.category}</p>
              <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{b.suburb ? `${b.suburb}, ` : ""}{b.state}</p>
            </div>
            <Rating value={b.rating} count={b.review_count} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {b.verified ? <VerifBadge type="verified" /> : <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">Not yet verified</span>}
          </div>
          {b.description && <p className="text-sm leading-relaxed text-foreground/80">{b.description}</p>}

          <div className="grid grid-cols-2 gap-2 pt-2 sm:grid-cols-4">
            {b.phone && <Button variant="outline" size="sm" onClick={() => track("phone_clicks", `tel:${b.phone}`)} data-testid="biz-call"><Phone className="h-4 w-4" />Call</Button>}
            {b.whatsapp && <Button variant="outline" size="sm" onClick={() => track("whatsapp_clicks", `https://wa.me/${b.whatsapp.replace(/\D/g, "")}`)} data-testid="biz-whatsapp-btn"><MessageCircle className="h-4 w-4" />WhatsApp</Button>}
            {b.website && <Button variant="outline" size="sm" onClick={() => track("website_clicks", b.website.startsWith("http") ? b.website : `https://${b.website}`)} data-testid="biz-website-btn"><Globe className="h-4 w-4" />Website</Button>}
            <Button variant="outline" size="sm" onClick={message} data-testid="biz-message"><MessageSquare className="h-4 w-4" />Message</Button>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={save} data-testid="biz-save"><Heart className={saved ? "h-4 w-4 fill-accent text-accent" : "h-4 w-4"} />Save</Button>
            <Button variant="ghost" size="sm" onClick={() => setShare(true)} data-testid="biz-share"><Share2 className="h-4 w-4" />Share</Button>
            <Button variant="ghost" size="sm" onClick={() => user ? setReport(true) : gate.require("Create a free account to report businesses.")} data-testid="biz-report"><Flag className="h-4 w-4" />Report</Button>
          </div>
          {isOwner && (
            <div className="flex flex-wrap gap-2 rounded-xl bg-secondary/60 p-3">
              {!featured && <Button size="sm" onClick={() => setFeature(true)} data-testid="biz-feature-btn"><Sparkles className="h-4 w-4" />Feature this business</Button>}
              {!b.verified && <Button size="sm" variant="outline" onClick={() => nav("/verification")} data-testid="biz-verify-btn">Verify business (ABN)</Button>}
            </div>
          )}
        </div>
      </div>

      {/* Reviews */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Reviews</h2>
        {user && !isOwner && !myReview && (
          <div className="mt-3 space-y-2 rounded-xl bg-secondary/40 p-3" data-testid="review-form">
            <p className="text-sm font-medium">Leave a review</p>
            <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setReviewRating(n)} data-testid={`review-star-${n}`}><Star className={n <= reviewRating ? "h-5 w-5 fill-amber-400 text-amber-400" : "h-5 w-5 text-muted-foreground"} /></button>)}</div>
            <Textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience" data-testid="review-text" />
            <Button size="sm" onClick={submitReview} data-testid="review-submit">Post review</Button>
          </div>
        )}
        <div className="mt-3 space-y-3">
          {reviews.length === 0 ? <p className="text-sm text-muted-foreground">No reviews yet.</p> : reviews.map((r) => {
            const mine = user && r.reviewer_id === user.id;
            return (
            <div key={r.id} data-testid={`review-${r.id}`} className={mine ? "rounded-xl border border-primary/30 bg-primary/5 p-3" : "border-t border-border pt-3"}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {r.reviewer_id ? <Link to={`/u/${r.reviewer_id}`} className="font-medium hover:underline" data-testid={`review-author-${r.id}`}>{r.reviewer_name}</Link> : <span className="font-medium">{r.reviewer_name}</span>}
                  {mine && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary" data-testid="your-review-badge">Your review</span>}
                </div>
                <Rating value={r.rating} />
              </div>
              {r.verified_interaction && <span className="text-[11px] font-semibold text-emerald-600">✓ Verified Interaction</span>}
              {mine && editing ? (
                <div className="mt-2 space-y-2" data-testid="review-edit-form">
                  <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setEditRating(n)} data-testid={`edit-star-${n}`}><Star className={n <= editRating ? "h-5 w-5 fill-amber-400 text-amber-400" : "h-5 w-5 text-muted-foreground"} /></button>)}</div>
                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} data-testid="review-edit-text" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit(r.id)} data-testid="review-edit-save">Save changes</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="review-edit-cancel">Cancel</Button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-sm text-foreground/80">{r.text}</p>
              )}
              {mine && !editing && (
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid="review-edit-btn"><Pencil className="h-3.5 w-3.5" />Edit</Button>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteReview(r.id)} data-testid="review-delete-btn"><Trash2 className="h-3.5 w-3.5" />Delete</Button>
                </div>
              )}
              {r.response && <p className="mt-2 rounded-lg bg-secondary/60 p-2 text-sm"><b>Owner:</b> {r.response}</p>}
            </div>
          );})}
        </div>
      </div>

      <ReportDialog open={report} onOpenChange={setReport} targetType="business" targetId={id} reportedUserId={b.owner_id} reportedUserName={b.name} onBlocked={() => nav("/explore")} />
      <ShareDialog open={share} onOpenChange={setShare} url={`/business/${id}`} title={b.name} />
      <CheckoutDialog open={feature} onOpenChange={setFeature} product={product} itemType="business" itemId={id} />
    </div>
  );
}
